// AnalyticsPage — pageview analytics for public lookbook pages.
//
// Data source: GET /api/analytics/stats?days=N (superuser-only, see
// analytics.pb.js). The endpoint returns top slugs, referrers, daily
// time series, language/screen breakdowns, and comment count.
//
// Layout: period tabs + 4 KPIs, daily views bar chart, then 2-column
// grid — top lookbooks | referrers + audience. Pure CSS bars, no chart lib.

import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/pb";
import { useT } from "../lib/i18n";

// ── Types ────────────────────────────────────────────────────────────────

interface SlugRow {
  slug: string;
  views: number;
}
interface RefRow {
  referrer: string;
  cnt: number;
}
interface TsRow {
  day: string;
  views: number;
}
interface LangRow {
  lang: string;
  cnt: number;
}
interface ScreenRow {
  screen: string;
  cnt: number;
}

interface StatsResponse {
  days: number;
  total: number;
  uniqueSlugs: number;
  totalComments: number;
  topSlugs: SlugRow[];
  topReferrers: RefRow[];
  timeSeries: TsRow[];
  topLangs: LangRow[];
  topScreens: ScreenRow[];
}

// Devlog session stats (отдельная коллекция devlog_sessions, см. analytics.pb.js)
interface ReelRow { reel: number; cnt: number }
interface DevlogStatsResponse {
  days: number;
  totalSessions: number;
  completedSessions: number;
  completionRate: number;          // 0-100
  avgDwellMs: number;
  maxDwellMs: number;
  avgScroll: number;               // 0-100
  reelDistribution: ReelRow[];
  timeSeries: TsRow[];
  topReferrers: RefRow[];
  topLangs: LangRow[];
  topScreens: ScreenRow[];
}

const PERIODS = [7, 30, 90] as const;
type Period = (typeof PERIODS)[number];

// ── Component ────────────────────────────────────────────────────────────

export default function AnalyticsPage() {
  const t = useT();
  const [period, setPeriod] = useState<Period>(30);
  const [data, setData] = useState<StatsResponse | null>(null);
  const [devlog, setDevlog] = useState<DevlogStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (days: number) => {
    setLoading(true);
    setError(null);
    try {
      const [r, d] = await Promise.all([
        api<StatsResponse>(`/api/analytics/stats?days=${days}`),
        api<DevlogStatsResponse>(`/api/analytics/devlog-stats?days=${days}`).catch(
          () => null as DevlogStatsResponse | null,
        ),
      ]);
      setData(r);
      setDevlog(d);
    } catch (e: unknown) {
      console.error("Analytics load failed:", e);
      setError(e instanceof Error ? e.message : "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(period);
  }, [period, load]);

  const avgPerDay = data ? Math.round(data.total / (data.days || 1)) : 0;

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">{t.analyticsTitle}</h1>
        <p className="page-subtitle">{t.analyticsSubtitle}</p>
      </div>

      {/* Period selector */}
      <div style={styles.periodRow}>
        {PERIODS.map((p) => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            style={{
              ...styles.periodBtn,
              ...(p === period ? styles.periodBtnActive : {}),
            }}
          >
            {p}{t.analyticsDays}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ color: "var(--danger)", fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* KPIs */}
      <div className="kpi-grid">
        <KpiCard label={t.analyticsKpiViews} value={data ? data.total.toLocaleString() : "..."} />
        <KpiCard label={t.analyticsKpiPages} value={data ? data.uniqueSlugs : "..."} />
        <KpiCard label={t.analyticsKpiAvgDay} value={data ? avgPerDay : "..."} />
        <KpiCard label={t.analyticsKpiComments} value={data ? data.totalComments : "..."} />
      </div>

      {loading ? (
        <div className="spinner-line">{t.loading}</div>
      ) : data ? (
        <>
          {/* Daily views chart */}
          <section className="card" style={{ padding: 18, marginBottom: 16 }}>
            <h2 style={sectionTitle}>{t.analyticsDailyViews}</h2>
            <DailyChart timeSeries={data.timeSeries} days={data.days} />
          </section>

          {/* Two-column grid */}
          <div style={styles.grid2}>
            {/* Top lookbooks */}
            <section className="card" style={{ padding: 18 }}>
              <h2 style={sectionTitle}>{t.analyticsTopPages}</h2>
              {data.topSlugs.length === 0 ? (
                <div className="muted" style={{ fontSize: 12 }}>{t.analyticsNoData}</div>
              ) : (
                <TopSlugsTable rows={data.topSlugs} />
              )}
            </section>

            {/* Right column: referrers + audience */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Referrers */}
              <section className="card" style={{ padding: 18 }}>
                <h2 style={sectionTitle}>{t.analyticsReferrers}</h2>
                {data.topReferrers.length === 0 ? (
                  <div className="muted" style={{ fontSize: 12 }}>{t.analyticsNoReferrers}</div>
                ) : (
                  <ReferrersList rows={data.topReferrers} />
                )}
              </section>

              {/* Audience: langs + screens */}
              <section className="card" style={{ padding: 18 }}>
                <h2 style={sectionTitle}>{t.analyticsAudience}</h2>
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                  <BreakdownList
                    title={t.analyticsLangs}
                    rows={data.topLangs.map((r) => ({ label: langName(r.lang), value: r.cnt }))}
                  />
                  <BreakdownList
                    title={t.analyticsScreens}
                    rows={data.topScreens.map((r) => ({ label: r.screen, value: r.cnt }))}
                  />
                </div>
              </section>
            </div>
          </div>

          {/* ── Devlog journal — отдельная section ─────────────────────── */}
          {devlog && (
            <section className="card" style={{ padding: 18, marginTop: 24 }}>
              <h2 style={{ ...sectionTitle, marginBottom: 4 }}>
                {t.analyticsDevlogTitle}
              </h2>
              <p className="muted" style={{ fontSize: 12, marginTop: 0, marginBottom: 18 }}>
                {t.analyticsDevlogSubtitle}
              </p>

              <div className="kpi-grid" style={{ marginBottom: 16 }}>
                <KpiCard
                  label={t.analyticsDevlogSessions}
                  value={devlog.totalSessions.toLocaleString()}
                />
                <KpiCard
                  label={t.analyticsDevlogCompleted}
                  value={`${devlog.completedSessions} (${devlog.completionRate}%)`}
                />
                <KpiCard
                  label={t.analyticsDevlogAvgDwell}
                  value={formatDwell(devlog.avgDwellMs)}
                />
                <KpiCard
                  label={t.analyticsDevlogAvgScroll}
                  value={`${devlog.avgScroll}%`}
                />
              </div>

              <div style={styles.grid2}>
                {/* Reel distribution */}
                <div>
                  <h3 style={subSectionTitle}>{t.analyticsDevlogReels}</h3>
                  <ReelDistribution rows={devlog.reelDistribution} total={devlog.totalSessions} />
                </div>

                {/* Daily chart */}
                <div>
                  <h3 style={subSectionTitle}>{t.analyticsDevlogTimeSeries}</h3>
                  {devlog.timeSeries.length === 0 ? (
                    <div className="muted" style={{ fontSize: 12 }}>{t.analyticsNoData}</div>
                  ) : (
                    <DailyChart timeSeries={devlog.timeSeries} days={devlog.days} />
                  )}
                </div>
              </div>

              {/* Audience for devlog */}
              {(devlog.topReferrers.length > 0 || devlog.topLangs.length > 0) && (
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginTop: 18 }}>
                  {devlog.topReferrers.length > 0 && (
                    <div style={{ flex: "1 1 220px", minWidth: 200 }}>
                      <h3 style={subSectionTitle}>{t.analyticsReferrers}</h3>
                      <ReferrersList rows={devlog.topReferrers} />
                    </div>
                  )}
                  <div style={{ flex: "1 1 220px", minWidth: 200 }}>
                    <h3 style={subSectionTitle}>{t.analyticsAudience}</h3>
                    <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                      <BreakdownList
                        title={t.analyticsLangs}
                        rows={devlog.topLangs.map((r) => ({ label: langName(r.lang), value: r.cnt }))}
                      />
                      <BreakdownList
                        title={t.analyticsScreens}
                        rows={devlog.topScreens.map((r) => ({ label: r.screen, value: r.cnt }))}
                      />
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}
        </>
      ) : null}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────

function KpiCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="kpi">
      <span className="kpi-label">{label}</span>
      <span className="kpi-value">{value}</span>
    </div>
  );
}

/** Pure CSS bar chart — one bar per day. */
function DailyChart({ timeSeries, days }: { timeSeries: TsRow[]; days: number }) {
  // Fill gaps: create an entry for every day in the range.
  const filled = fillDays(timeSeries, days);
  const max = Math.max(1, ...filled.map((d) => d.views));

  if (filled.length === 0) {
    return <div className="muted" style={{ fontSize: 12, padding: "20px 0" }}>No data yet.</div>;
  }

  return (
    <div style={styles.chartWrap}>
      <div style={{ ...styles.chartGrid, gridTemplateColumns: `repeat(${filled.length}, 1fr)` }}>
        {filled.map((d, i) => {
          const pct = (d.views / max) * 100;
          const isWeekend = new Date(d.day + "T00:00:00").getDay() % 6 === 0;
          return (
            <div key={i} style={styles.chartCol} title={`${d.day}: ${d.views}`}>
              <div
                style={{
                  ...styles.chartBar,
                  height: `${Math.max(pct, 2)}%`,
                  opacity: isWeekend ? 0.5 : 1,
                }}
              />
            </div>
          );
        })}
      </div>
      {/* X-axis labels */}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
        <span style={styles.chartLabel}>{filled[0]?.day ? shortDate(filled[0].day) : ""}</span>
        {filled.length > 2 && (
          <span style={styles.chartLabel}>
            {shortDate(filled[Math.floor(filled.length / 2)].day)}
          </span>
        )}
        <span style={styles.chartLabel}>{shortDate(filled[filled.length - 1].day)}</span>
      </div>
    </div>
  );
}

function TopSlugsTable({ rows }: { rows: SlugRow[] }) {
  const max = rows.length > 0 ? rows[0].views : 1;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {rows.slice(0, 20).map((r, i) => {
        const pct = (r.views / max) * 100;
        const slug = r.slug || "(index)";
        return (
          <div key={i} style={styles.slugRow}>
            <div style={styles.slugRank}>{i + 1}</div>
            <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
              <div
                style={{
                  ...styles.slugBar,
                  width: `${pct}%`,
                }}
              />
              <a
                href={`https://reverium.app/${slug}`}
                target="_blank"
                rel="noopener"
                style={styles.slugName}
                title={slug}
              >
                /{slug}
              </a>
            </div>
            <div style={styles.slugViews}>{r.views}</div>
          </div>
        );
      })}
    </div>
  );
}

function ReferrersList({ rows }: { rows: RefRow[] }) {
  const max = rows.length > 0 ? rows[0].cnt : 1;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {rows.slice(0, 10).map((r, i) => {
        const pct = (r.cnt / max) * 100;
        const label = prettifyReferrer(r.referrer);
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
              <div style={{ ...styles.refBar, width: `${pct}%` }} />
              <span style={styles.refLabel} title={r.referrer}>{label}</span>
            </div>
            <span style={styles.refCnt}>{r.cnt}</span>
          </div>
        );
      })}
    </div>
  );
}

function BreakdownList({
  title,
  rows,
}: {
  title: string;
  rows: { label: string; value: number }[];
}) {
  if (rows.length === 0) return null;
  const total = rows.reduce((s, r) => s + r.value, 0) || 1;
  return (
    <div style={{ flex: 1, minWidth: 140 }}>
      <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 8, fontWeight: 500, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>
        {title}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {rows.slice(0, 8).map((r, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--text-1)" }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>{r.label}</span>
            <span style={{ color: "var(--text-3)", fontVariantNumeric: "tabular-nums", marginLeft: 8, flexShrink: 0 }}>
              {Math.round((r.value / total) * 100)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Reel distribution — горизонтальные бары по 3 reel'ам с подписями
// «дочитал до Reel N» + процент от total. Если furthest_reel_num=0
// (открыли и сразу ушли) — отдельная строка «Не дошёл до Reel 1».
function ReelDistribution({ rows, total }: { rows: ReelRow[]; total: number }) {
  // нормализуем: всегда показываем все 4 «бакета» (0, 1, 2, 3)
  const map = new Map<number, number>();
  rows.forEach((r) => map.set(r.reel, r.cnt));
  const buckets = [0, 1, 2, 3].map((reel) => ({
    reel,
    cnt: map.get(reel) || 0,
  }));
  const denom = total > 0 ? total : 1;
  const labels: Record<number, string> = {
    0: "Не дошёл до Reel 1",
    1: "Reel 1 · Просьба и фундамент",
    2: "Reel 2 · Голоса, доски, поиск",
    3: "Reel 3 · Полировка и юзеры",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {buckets.map((b) => {
        const pct = (b.cnt / denom) * 100;
        return (
          <div key={b.reel} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 12, color: "var(--text-2)", width: 220, flexShrink: 0 }}>
              {labels[b.reel]}
            </span>
            <div style={{ flex: 1, height: 10, background: "var(--bg-1)", borderRadius: 3, position: "relative" }}>
              <div
                style={{
                  position: "absolute", inset: 0,
                  width: `${pct}%`,
                  background: b.reel === 0 ? "var(--text-4, #4b4434)" : "var(--accent, #c08a4a)",
                  borderRadius: 3,
                  transition: "width 0.3s",
                }}
              />
            </div>
            <span style={{
              fontSize: 12, fontVariantNumeric: "tabular-nums",
              color: "var(--text-1)", width: 86, textAlign: "right", flexShrink: 0,
            }}>
              {b.cnt} <span style={{ color: "var(--text-3)" }}>({Math.round(pct)}%)</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

function formatDwell(ms: number): string {
  if (!ms || ms < 1000) return "0с";
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}с`;
  const min = Math.floor(sec / 60);
  const remSec = sec % 60;
  if (min < 60) return remSec > 0 ? `${min}м ${remSec}с` : `${min}м`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  return `${hr}ч ${remMin}м`;
}

function fillDays(ts: TsRow[], days: number): TsRow[] {
  const map = new Map<string, number>();
  for (const r of ts) {
    map.set(r.day, r.views);
  }
  const result: TsRow[] = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    result.push({ day: key, views: map.get(key) || 0 });
  }
  return result;
}

function shortDate(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function langName(code: string): string {
  const map: Record<string, string> = {
    ru: "Russian", en: "English", "en-US": "English (US)", "en-GB": "English (GB)",
    "ru-RU": "Russian", de: "German", fr: "French", es: "Spanish",
    uk: "Ukrainian", pl: "Polish", ja: "Japanese", ko: "Korean",
    zh: "Chinese", pt: "Portuguese", it: "Italian", tr: "Turkish",
  };
  return map[code] || code;
}

function prettifyReferrer(ref: string): string {
  try {
    const u = new URL(ref);
    return u.hostname.replace(/^www\./, "");
  } catch {
    return ref.slice(0, 40);
  }
}

// ── Inline styles ────────────────────────────────────────────────────────

const subSectionTitle: React.CSSProperties = {
  fontFamily: "var(--font-serif)",
  fontSize: 13,
  fontWeight: 600,
  margin: "0 0 10px",
  color: "var(--text-1)",
  letterSpacing: "-0.005em",
};

const sectionTitle: React.CSSProperties = {
  fontFamily: "var(--font-serif)",
  fontSize: 16,
  fontWeight: 600,
  margin: "0 0 14px",
  color: "var(--text-0)",
  letterSpacing: "-0.01em",
};

const styles: Record<string, React.CSSProperties> = {
  periodRow: {
    display: "flex",
    gap: 4,
    marginBottom: 20,
    padding: 3,
    background: "var(--bg-2)",
    border: "1px solid var(--border-1)",
    borderRadius: "var(--radius-md)",
    width: "fit-content",
  },
  periodBtn: {
    background: "transparent",
    border: 0,
    color: "var(--text-2)",
    fontSize: 12,
    fontWeight: 500,
    padding: "6px 14px",
    borderRadius: "var(--radius-sm)",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "var(--transition)",
  },
  periodBtnActive: {
    background: "var(--bg-3)",
    color: "var(--text-0)",
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "1.2fr 1fr",
    gap: 16,
  },
  chartWrap: {
    padding: "8px 0 0",
  },
  chartGrid: {
    display: "grid",
    gap: 2,
    height: 120,
    alignItems: "flex-end",
  },
  chartCol: {
    display: "flex",
    alignItems: "flex-end",
    height: "100%",
  },
  chartBar: {
    width: "100%",
    background: "var(--accent)",
    borderRadius: "2px 2px 0 0",
    transition: "height 0.3s",
    minHeight: 1,
  },
  chartLabel: {
    fontSize: 10,
    color: "var(--text-3)",
    fontVariantNumeric: "tabular-nums",
  },
  slugRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "5px 0",
    borderBottom: "1px solid var(--border-1)",
  },
  slugRank: {
    width: 22,
    fontSize: 11,
    color: "var(--text-3)",
    textAlign: "right" as const,
    fontVariantNumeric: "tabular-nums",
    flexShrink: 0,
  },
  slugBar: {
    position: "absolute" as const,
    top: 0,
    left: 0,
    height: "100%",
    background: "var(--accent-soft)",
    borderRadius: "var(--radius-sm)",
  },
  slugName: {
    position: "relative" as const,
    display: "block",
    fontSize: 12,
    color: "var(--text-1)",
    fontFamily: "var(--font-mono)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
    padding: "3px 6px",
    textDecoration: "none",
  },
  slugViews: {
    fontSize: 12,
    color: "var(--accent)",
    fontVariantNumeric: "tabular-nums",
    fontWeight: 500,
    flexShrink: 0,
    minWidth: 36,
    textAlign: "right" as const,
  },
  refBar: {
    position: "absolute" as const,
    top: 0,
    left: 0,
    height: "100%",
    background: "var(--accent-soft)",
    borderRadius: "var(--radius-sm)",
  },
  refLabel: {
    position: "relative" as const,
    display: "block",
    fontSize: 12,
    color: "var(--text-1)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
    padding: "3px 6px",
  },
  refCnt: {
    fontSize: 12,
    color: "var(--text-2)",
    fontVariantNumeric: "tabular-nums",
    flexShrink: 0,
    minWidth: 30,
    textAlign: "right" as const,
  },
};
